/*
 * @Author: chenchaoqiang@leedarson.com
 * @Date: 2022-08-02 17:27:05
 * Copyright 2022 Leedarson, All Rights Reserved.
 */
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import PageLoading from '@root/components/PageLoading';
import PageLoadError from '@root/components/PageLoadError';
import SafeAreaInset from '@root/components/SafeAreaInset';
import { IOrderGood } from '@root/types/bill';
import { CellCard, CellCommon, CellTitle } from '@root/components/Cell';
import { DATE_FORMAT, DATE_TIME_FORMAT, EPayMode } from '@root/constant';
import useBillDetail from '@root/hooks/bill/useBillDetail';
import './style.less';

const BillDetail = () => {
  const { t } = useTranslation();
  const { detail, loading, getDetail } = useBillDetail();

  const {
    payPrice,
    orderStatus,
    invoice,
    invoiceDate,
    invoiceEmail,
    payEmail,
    orderGoodsResp,
    type,
    giftCardCode,
    activateTime,
    payMode,
    tax,
    orderPrice,
    refundDetailResp,
  } = detail;

  const payModeText: any = useMemo(() => {
    return {
      [EPayMode.PayPal]: t('payPal'),
      [EPayMode.ApplePay]: t('apple'),
      [EPayMode.CreditCard]: t('credit_card'),
    };
  }, [t]);

  const payStatus = useMemo(() => {
    if (orderStatus === 5) {
      return t('bill_detail_paid');
    }
    if (orderStatus === 8) {
      return t('bill_detail_refunded');
    }
    if (orderStatus === 9) {
      return t('bill_detail_refunded_failed');
    }
    return null;
  }, [orderStatus, t]);

  if (loading < 1) {
    return <PageLoading />;
  }

  if (loading === 2) {
    return <PageLoadError onTryAgain={getDetail} />;
  }

  const { subscribeStartDate, subscribeEndDate } =
    orderGoodsResp?.[0] || ({} as IOrderGood);

  const { refundId, refundTime, refundAmount } = refundDetailResp || {};

  return (
    <>
      <div className="bill-detail">
        <CellCard marginBottom={0}>
          <CellCommon
            inner
            title={
              <h2 className="amount">
                ${payPrice} <span>{t('bill_detail_amount')}</span>
              </h2>
            }
          >
            {payStatus && <span className="status">{payStatus}</span>}
          </CellCommon>
          <CellCommon inner title={t('invoice')}>
            {invoice || ''}
          </CellCommon>
          <CellCommon inner title={t('bill_detail_invoice_date')}>
            {invoiceDate ? dayjs(invoiceDate).format(DATE_TIME_FORMAT) : ''}
          </CellCommon>
          <CellCommon inner title={t('bill_detail_invoice_to')}>
            {invoiceEmail || ''}
          </CellCommon>
          <CellCommon inner title={t('bill_detail_pay_to')}>
            {payEmail || ''}
          </CellCommon>
          <CellCommon inner title={t('bill_detail_service_period')}>
            {subscribeStartDate
              ? dayjs(subscribeStartDate).format(DATE_FORMAT)
              : ''}
            -
            {subscribeEndDate
              ? dayjs(subscribeEndDate).format(DATE_FORMAT)
              : ''}
          </CellCommon>
          <div className="bill-table">
            <p className="bill-table-header">{t('bill_detail_service')}</p>
            <p className="bill-table-header">{t('total')}</p>
            {orderGoodsResp?.map((i) => {
              return (
                <Fragment key={i.goodsId}>
                  <p className="bill-table-cell">
                    {t('bill_detail_plan_name')}
                    {i.subscribeName}
                  </p>
                  <p className="bill-table-cell">${i.price}</p>
                </Fragment>
              );
            })}
          </div>
        </CellCard>
      </div>
      <div className="bill-payment">
        <CellTitle>{t('bill_detail_payment')}</CellTitle>
        <CellCard marginTop={0} marginBottom={0}>
          {/* 礼品卡 */}
          {type === 'giftCard' && (
            <CellCommon
              inner
              title={t('gift_card')}
              description={giftCardCode || ''}
            >
              {activateTime ? dayjs(activateTime).format(DATE_FORMAT) : ''}
            </CellCommon>
          )}

          {/* 自动发放服务 */}
          {type === 'ruleGive' && (
            <CellCommon inner title={t('trial_service')}>
              {activateTime ? dayjs(activateTime).format(DATE_FORMAT) : ''}
            </CellCommon>
          )}

          {/* PayPal Apple Pay payMode 1-paypal 2-apple pay */}
          {(type === 'buy' || type === 'renew') && (
            <CellCommon
              inner
              title={payModeText[payMode] || ''}
              description={invoice || ''}
            >
              {invoiceDate ? dayjs(invoiceDate).format(DATE_TIME_FORMAT) : ''}
            </CellCommon>
          )}
          {/* 免费套餐 */}
          {type === 'trial' && (
            <CellCommon inner title={t('bill_detail_free_trial')}>
              {invoiceDate ? dayjs(invoiceDate).format(DATE_FORMAT) : ''}
            </CellCommon>
          )}
          <CellCommon inner title={t('bill_detail_subtotal')}>
            {orderPrice || ''}
          </CellCommon>
          <CellCommon inner title={t('bill_detail_tax')}>
            {tax || ''}
          </CellCommon>
          <CellCommon inner title={t('total')}>
            {payPrice || ''}
          </CellCommon>
        </CellCard>

        {/* 退款状态 */}
        {(orderStatus === 8 || orderStatus === 9) && (
          <>
            <CellTitle>{t('bill_detail_refunded')}</CellTitle>
            <CellCard marginTop={0} marginBottom={0}>
              <CellCommon inner title={payModeText[payMode] || ''}>
                {refundId || ''}
              </CellCommon>
              <CellCommon inner title={t('bill_detail_refunded_date')}>
                {refundTime ? dayjs(refundTime).format(DATE_TIME_FORMAT) : ''}
              </CellCommon>
              <CellCommon inner title={t('bill_detail_amount')}>
                -${refundAmount}
              </CellCommon>
            </CellCard>
          </>
        )}
        <SafeAreaInset />
      </div>
    </>
  );
};

export default BillDetail;
